import { useCallback, useState } from 'react';
import useQueryParamState from './useQueryParamState';

/**
 * Specialized hook for managing list page state with URL synchronization
 * Combines common list functionality like pagination, sorting, and filtering
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.searchParam - Query param name for search (default: 'search')
 * @param {string} config.statusParam - Query param name for status filter (default: 'status')
 * @param {string} config.pageParam - Query param name for page (default: 'page')
 * @param {string} config.sizeParam - Query param name for page size (default: 'size')
 * @param {string} config.sortByParam - Query param name for sort field (default: 'sortBy')
 * @param {string} config.sortOrderParam - Query param name for sort order (default: 'sortOrder')
 * @param {Object} config.defaults - Default values
 * @param {string} config.defaults.search - Default search value
 * @param {string} config.defaults.status - Default status filter
 * @param {number} config.defaults.page - Default page number
 * @param {number} config.defaults.size - Default page size
 * @param {string} config.defaults.sortBy - Default sort field
 * @param {string} config.defaults.sortOrder - Default sort order
 * @param {Object} config.options - Additional options
 * @param {number} config.options.searchDebounceMs - Debounce delay for search (default: 500)
 * @returns {Object} - Object containing all state and handlers
 */
const useListState = (config = {}) => {
  const {
    searchParam = 'search',
    statusParam = 'status',
    pageParam = null,
    sizeParam = null,
    sortByParam = 'sortBy',
    sortOrderParam = 'sortOrder',
    defaults = {},
    options = {}
  } = config;

  const {
    search: defaultSearch = '',
    status: defaultStatus = '',
    page: defaultPage = 0,
    size: defaultSize = 10,
    sortBy: defaultSortBy = 'createdAt',
    sortOrder: defaultSortOrder = 'desc'
  } = defaults;

  const {
    searchDebounceMs = 500
  } = options;

  // Individual query param states
  const [searchQuery, setSearchQuery] = useQueryParamState(searchParam, defaultSearch, {
    debounceMs: searchDebounceMs,
    replace: true
  });
  
  const [statusFilter, setStatusFilter] = useQueryParamState(statusParam, defaultStatus, {
    replace: true
  });
  
  const [page, setPage] = useState(defaultPage);
  const [rowsPerPage, setRowsPerPage] = useState(defaultSize);
  
  const [orderBy, setOrderBy] = useQueryParamState(sortByParam, defaultSortBy, {
    replace: true
  });
  
  const [order, setOrder] = useQueryParamState(sortOrderParam, defaultSortOrder, {
    replace: true
  });

  // Computed values
  const sort = `${orderBy},${order}`;

  // Event handlers with defensive programming
  const handleSearchChange = useCallback((event) => {
    if (event && event.target && typeof event.target.value !== 'undefined') {
      setSearchQuery(event.target.value);
    }
  }, [setSearchQuery]);

  const handleStatusFilterChange = useCallback((event) => {
    if (event && event.target && typeof event.target.value !== 'undefined') {
      setStatusFilter(event.target.value);
    }
  }, [setStatusFilter]);

  const handlePageChange = useCallback((event, newPage) => {
    if (typeof newPage === 'number' && !isNaN(newPage) && newPage >= 0) {
      setPage(newPage);
    }
  }, [setPage]);

  const handleRowsPerPageChange = useCallback((event) => {
    if (event && event.target && event.target.value) {
      const newSize = parseInt(event.target.value, 10);
      if (!isNaN(newSize) && newSize > 0) {
        // Using setTimeout to ensure these state updates don't batch
        setRowsPerPage(newSize);
        
        // Reset to first page when changing page size
        setTimeout(() => {
          setPage(0); 
        }, 0);
      }
    }
  }, [setRowsPerPage, setPage]);

  const handleSortChange = useCallback((property) => {
    if (typeof property === 'string' && property) {
      const isAsc = orderBy === property && order === 'asc';
      
      // Use timeouts to ensure these happen in sequence
      setOrder(isAsc ? 'desc' : 'asc');
      
      setTimeout(() => {
        setOrderBy(property);
      }, 0);
      
      // Reset to first page when sorting
      setTimeout(() => {
        setPage(0);
      }, 0);
    }
  }, [orderBy, order, setOrder, setOrderBy, setPage]);

  const clearFilters = useCallback(() => {
    setSearchQuery(defaultSearch);
    setTimeout(() => {
      setStatusFilter(defaultStatus);
    }, 0);
    setTimeout(() => {
      setPage(defaultPage);
    }, 0);
  }, [setSearchQuery, setStatusFilter, setPage, defaultSearch, defaultStatus, defaultPage]);

  const clearSearch = useCallback(() => {
    setSearchQuery(defaultSearch);
  }, [setSearchQuery, defaultSearch]);

  // Build filters object for API calls
  const buildFilters = useCallback(() => {
    // Filter out undefined or empty string values
    const filters = {
      page,
      size: rowsPerPage,
      sort
    };
    
    if (statusFilter) filters.status = statusFilter;
    if (searchQuery) filters.anyMatch = searchQuery;
    
    return filters;
  }, [statusFilter, searchQuery, page, rowsPerPage, sort]);

  return {
    // State values
    searchQuery,
    statusFilter,
    page,
    rowsPerPage,
    orderBy,
    order,
    sort,
    
    // Setters
    setSearchQuery,
    setStatusFilter,
    setPage,
    setRowsPerPage,
    setOrderBy,
    setOrder,
    
    // Event handlers
    handleSearchChange,
    handleStatusFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    
    // Utility functions
    clearFilters,
    clearSearch,
    buildFilters
  };
};

export default useListState;